'use client';

import { uploadViaProxy } from '@/lib/uploads/upload-via-proxy';

export const CONTACT_UPLOAD_TIMEOUT_MS = 60_000;
export const CONTACT_UPLOAD_MAX_BYTES = 10 * 1024 * 1024;
export const CONTACT_IMAGE_ACCEPT =
  'image/png,image/jpeg,image/jpg,image/webp,image/gif';

export interface PendingContactUpload {
  id: string;
  file: File;
}

export interface ContactUploadClaim {
  storageKey: string;
  uploadToken: string;
}

export interface ContactUploadResult extends ContactUploadClaim {
  id: string;
  url?: string;
}

interface GeneratedContactUpload extends ContactUploadClaim {
  uploadUrl: string;
  publicUrl?: string;
}

export async function cleanupContactUploadClaims(args: {
  sessionId: string;
  claims: ContactUploadClaim[];
  cleanup: (input: {
    sessionId: string;
    attachments: ContactUploadClaim[];
  }) => Promise<unknown>;
}): Promise<void> {
  if (args.claims.length === 0) return;
  await args.cleanup({
    sessionId: args.sessionId,
    attachments: args.claims,
  });
}

export async function uploadContactAttachmentBatch(args: {
  attachments: PendingContactUpload[];
  sessionId: string;
  generateUploadUrl: (input: {
    filename: string;
    contentType: string;
    sessionId: string;
  }) => Promise<GeneratedContactUpload>;
  cleanup: (input: {
    sessionId: string;
    attachments: ContactUploadClaim[];
  }) => Promise<unknown>;
}): Promise<ContactUploadResult[]> {
  const issuedClaims: ContactUploadClaim[] = [];
  const attempts = await Promise.allSettled(
    args.attachments.map(async (attachment): Promise<ContactUploadResult> => {
      const generated = await args.generateUploadUrl({
        filename: attachment.file.name,
        contentType: attachment.file.type,
        sessionId: args.sessionId,
      });
      issuedClaims.push({
        storageKey: generated.storageKey,
        uploadToken: generated.uploadToken,
      });
      const response = await uploadViaProxy(
        generated.uploadUrl,
        attachment.file,
        attachment.file.type,
        { timeoutMs: CONTACT_UPLOAD_TIMEOUT_MS }
      );
      if (!response.ok) throw new Error('CONTACT_UPLOAD_FAILED');
      return {
        id: attachment.id,
        storageKey: generated.storageKey,
        uploadToken: generated.uploadToken,
        ...(generated.publicUrl ? { url: generated.publicUrl } : {}),
      };
    })
  );
  const failed = attempts.find(
    (attempt): attempt is PromiseRejectedResult => attempt.status === 'rejected'
  );
  if (failed) {
    await cleanupContactUploadClaims({
      sessionId: args.sessionId,
      claims: issuedClaims,
      cleanup: args.cleanup,
    }).catch(() => undefined);
    throw failed.reason;
  }
  return attempts.flatMap((attempt) =>
    attempt.status === 'fulfilled' ? [attempt.value] : []
  );
}
